import { useUserProfileProviderContext } from "@jugl-web/domain-resources/users/providers/UserProfileProvider";
import { useRestApiProvider, UserProfile } from "@jugl-web/rest-api";
import {
  Button,
  DataLoadingWrapper,
  Dialog,
  TextInput,
} from "@jugl-web/ui-components";
import {
  useFormFieldsValidators,
  useToast,
  useTranslations,
} from "@jugl-web/utils";
import { useFormSubmittingState } from "@jugl-web/utils/form/hooks/useFormSubmittingState";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";

type Inputs = Pick<UserProfile["general"], "first_name" | "last_name">;

export const EditProfileNameDialog: React.FC<{
  isOpen: boolean;
  onRequestClose: () => void;
}> = ({ isOpen, onRequestClose }) => {
  const { t } = useTranslations();
  const { toast } = useToast();
  const { entityId } = useEntitySelectedProvider();
  const { usersApi } = useRestApiProvider();
  const { requiredValidator } = useFormFieldsValidators();
  const { data, isLoading, isFetching, isError, refetch } =
    usersApi.useGetUserProfileQuery({}, { skip: !isOpen });

  const { updateUserProfileName } = useUserProfileProviderContext();
  const { isSubmitting, setIsSubmitting } = useFormSubmittingState();

  const {
    handleSubmit,
    reset,
    formState: { isDirty, errors },
    control,
  } = useForm<Inputs>({
    defaultValues: {
      first_name: "",
      last_name: "",
    },
  });

  useEffect(() => {
    if (data && isOpen) {
      reset({
        first_name: data.general.first_name,
        last_name: data.general.last_name,
      });
    }
  }, [data, reset, isOpen]);

  const onSubmit = async (values: Inputs) => {
    if (!data) return;

    const { first_name: firstName, last_name: lastName } = values;

    try {
      setIsSubmitting(true);

      await updateUserProfileName({
        entityId,
        generalProfileId: data.general.id,
        userId: data.user_id,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });

      toast(
        t({
          id: "settings-page.profile-name-updated",
          defaultMessage: "Profile name was updated",
        })
      );
      onRequestClose();
    } catch {
      toast(
        t({
          id: "feedback.something-went-wrong",
          defaultMessage: "Something went wrong",
        }),
        { variant: "error" }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      className="flex h-[400px] w-[450px] flex-col items-center justify-center gap-12 px-6 py-10"
      isOpen={isOpen}
      onClose={onRequestClose}
    >
      <DataLoadingWrapper
        isLoading={isLoading || isFetching}
        isError={isError}
        onRetry={refetch}
      >
        <div className="flex h-full w-full flex-col items-center justify-center gap-12">
          <span className="font-secondary text-lg font-medium text-dark">
            {t({
              id: "settings-page.edit-profile-name",
              defaultMessage: "Edit Profile Name",
            })}
          </span>
          <div className="flex w-full flex-col gap-4">
            <Controller
              name="first_name"
              control={control}
              rules={{ ...requiredValidator() }}
              render={({ field }) => (
                <TextInput
                  label={t({
                    id: "settings-page.first-name",
                    defaultMessage: "First Name",
                  })}
                  labelPosition="border-overlap"
                  isRequired
                  autoFocus
                  {...field}
                  onChange={(e) => field.onChange(e.target.value.trimStart())}
                  isInvalid={!!errors.first_name}
                  errorMessage={errors.first_name?.message}
                />
              )}
            />
            <Controller
              name="last_name"
              control={control}
              rules={{ ...requiredValidator() }}
              render={({ field }) => (
                <TextInput
                  label={t({
                    id: "settings-page.last-name",
                    defaultMessage: "Last Name",
                  })}
                  labelPosition="border-overlap"
                  isRequired
                  {...field}
                  onChange={(e) => field.onChange(e.target.value.trimStart())}
                  isInvalid={!!errors.last_name}
                  errorMessage={errors.last_name?.message}
                />
              )}
            />
          </div>
          <div className="flex w-full items-center gap-4">
            <Button
              type="button"
              color="grey"
              className="h-10 shrink-0"
              onClick={onRequestClose}
              isDisabled={isSubmitting}
            >
              {t({
                id: "common.cancel",
                defaultMessage: "Cancel",
              })}
            </Button>
            <Button
              className="h-10 flex-1"
              color="dark"
              iconEnd={<ArrowIcon />}
              isDisabled={!isDirty || isSubmitting}
              onClick={handleSubmit(onSubmit)}
            >
              {t({
                id: "common.update",
                defaultMessage: "Update",
              })}
            </Button>
          </div>
        </div>
      </DataLoadingWrapper>
    </Dialog>
  );
};
